import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import SwalToast from '../../swalToast';

const MAX_LENGTH = 1000;

const CommentsModal = ({
  isViewMode,
  attributeID,
  entityId,
  currentRowData,
  comments,
  setComments,
  openCommentBox,
  setOpenCommentBox,
  entityType,
}) => {
  const { entity_id: row_entity_id, unique_id } = currentRowData || {};
  const { saveModelComments } = useAttribute();
  const [comment, setComment] = useState('');

  const generatePayload = () => {
    return {
      entity_id: entityId,
      entity_type: entityType,
      attribute_id: attributeID,
      entityComments: {
        [row_entity_id]: comment,
      },
    };
  };

  useEffect(() => {
    setComment(comments[row_entity_id] || '');
  }, [row_entity_id, comments, openCommentBox]);

  const handleClose = useCallback(() => {
    setComment('');
    setOpenCommentBox(false);
  }, [setOpenCommentBox]);

  const handleChange = useCallback((e) => {
    const comment_ = e?.target?.value;
    if (comment_?.length <= MAX_LENGTH) {
      setComment(comment_);
    }
  }, []);

  const handleSave = useCallback(() => {
    saveModelComments(generatePayload())
      .then(() => {
        setComments((prev) => ({
          ...prev,
          [row_entity_id]: comment,
        }));
        SwalToast({
          icon: 'success',
          title: `Saved comment for "${unique_id}" model.`,
          id: `comment_success_toast_${row_entity_id}`,
        });
      })
      .catch(() => {
        SwalToast({
          icon: 'error',
          title: `Failed to save comment for "${unique_id}" model.`,
          id: `comment_failure_toast_${row_entity_id}`,
        });
      })
      .finally(() => {
        handleClose();
      });
  }, [row_entity_id, comment, handleClose]);

  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <Dialog
      onClose={handleClose}
      open={openCommentBox}
      maxWidth="sm"
      fullWidth={true}
      aria-labelledby="comment-title"
    >
      <DialogTitle id="comment-title">
        <Box component="span" aria-live="polite" aria-atomic="true">
          Add Attestation Comments For Selected Model
        </Box>
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ paddingBottom: 0 }}>
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Attestation comment:"
          value={comment}
          onChange={handleChange}
          onDoubleClick={stopPropagation}
          disabled={isViewMode}
        />
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'end',
            my: 1,
          }}
        >
          {comment?.length} / {MAX_LENGTH}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" title="Cancel">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          title="Save"
          disabled={isViewMode}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommentsModal;

CommentsModal.propTypes = {
  isViewMode: PropTypes.bool.isRequired,
  attributeID: PropTypes.number.isRequired,
  entityId: PropTypes.string.isRequired,
  currentRowData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  comments: PropTypes.oneOfType([PropTypes.object]).isRequired,
  setComments: PropTypes.func.isRequired,
  openCommentBox: PropTypes.bool.isRequired,
  setOpenCommentBox: PropTypes.func.isRequired,
  entityType: PropTypes.string.isRequired,
};
